import Component from 'flarum/Component';
import Button from 'flarum/components/Button';
import ChatEditModal from './ChatEditModal';
import ChatMentionDropdown from './ChatMentionDropdown';
import { throttle } from 'flarum/utils/throttleDebounce';

export default class ChatInput extends Component {
    oninit(vnode) {
        super.oninit(vnode);

        this.model = this.attrs.model;
        this.state = this.attrs.state;

        app.chat.input = this;

        this.messageCharLimit = app.forum.attribute('xelson-chat.settings.charlimit') ?? 512;

        this.mentionSuggestions = null;
        this.mentionIndex = 0;
        this.mentionStart = 0;
        this.mentionCursorEnd = 0; // cursor position when mention was detected
        this.mentionType = null; // 'user' or 'game'
        this.gameSearchTimeout = null;

        this.uploading = false;
        this.expanded = false;

        this.emojiAreaLoading = false;
        this.emojiArea = null;
        this.emojiPickerVisible = false;

        this.updatePlaceholder();
    }

    oncreate(vnode) {
        super.oncreate(vnode);

        let inputState = this.state.input;
        let input = this.$('#chat-input')[0];
        input.lineHeight = parseInt(window.getComputedStyle(input).getPropertyValue('line-height'));
        inputState.element = input;

        if (inputState.content() && inputState.content().length) {
            this.inputProcess({ target: input });
        }

        this.updateLimit();
    }

    onbeforeupdate(vnode, old) {
        super.onbeforeupdate(vnode, old);

        if (this.model !== this.attrs.model) {
            this.model = this.attrs.model;
            this.state = this.attrs.state;
        }
        this.updatePlaceholder();
    }

    updatePlaceholder() {
        if (!app.session.user) this.inputPlaceholder = app.translator.trans('xelson-chat.forum.errors.unauthenticated');
        else if (!app.chat.getPermissions().post) this.inputPlaceholder = app.translator.trans('xelson-chat.forum.errors.chatdenied');
        else if (this.model.removed_at()) this.inputPlaceholder = app.translator.trans('xelson-chat.forum.errors.removed');
        else this.inputPlaceholder = app.translator.trans('xelson-chat.forum.chat.placeholder');
    }

    view() {
        const isMobileExpanded = this.expanded && app.screen() === 'phone';
        return (
            <div className={'ChatInput input-wrapper' + (isMobileExpanded ? ' ChatInput--expanded' : '')}>
                {isMobileExpanded ? (
                    <div className="ChatInput-backdrop" onclick={this.collapseInput.bind(this)} />
                ) : null}
                <ChatMentionDropdown
                    suggestions={this.mentionSuggestions}
                    selectedIndex={this.mentionIndex}
                    onSelect={this.selectMention.bind(this)}
                />
                <textarea
                    id="chat-input"
                    maxlength={this.messageCharLimit}
                    disabled={!app.chat.getPermissions().post || this.model.removed_at()}
                    placeholder={this.inputPlaceholder}
                    onkeydown={this.inputKeyDown.bind(this)}
                    onkeypress={this.inputPressEnter.bind(this)}
                    oninput={this.inputProcess.bind(this)}
                    onpaste={this.inputProcess.bind(this)}
                    onkeyup={this.inputSaveDraft.bind(this)}
                    onfocus={this.onInputFocus.bind(this)}
                    onblur={this.onInputBlur.bind(this)}
                    rows={this.state.input.rows}
                    value={this.state.input.content()}
                    onupdate={() => this.saveDraft.apply(this)}
                />
                {this.state.messageEditing ? (
                    <div className="icon edit" onclick={this.state.messageEditEnd.bind(this.state)}>
                        <i class="fas fa-times"></i>
                    </div>
                ) : null}
                {this.model.removed_at() && this.model.removed_by() === parseInt(app.session.user.id()) ? (
                    <Button className="Button Button--primary ButtonRejoin" onclick={() => app.modal.show(ChatEditModal, { model: this.model })}>
                        {app.translator.trans('xelson-chat.forum.chat.rejoin')}
                    </Button>
                ) : (
                    [
                        <div className="ChatInput-icons">
                            <div className="icon emoji" onclick={this.toggleEmojiPicker.bind(this)}>
                                <i class={this.emojiAreaLoading ? "fas fa-spinner fa-spin" : "far fa-smile-beam"}></i>
                            </div>
                            <div className="icon upload" onclick={() => this.fileInput && this.fileInput.click()}>
                                <i class={this.uploading ? "fas fa-spinner fa-spin" : "fas fa-paperclip"}></i>
                            </div>
                            <div className="icon send" onclick={this.inputPressButton.bind(this)}>
                                <i class="fas fa-angle-double-right"></i>
                            </div>
                        </div>,
                        <input
                            type="file"
                            accept="image/*"
                            style={{display: 'none'}}
                            oncreate={(vnode) => { this.fileInput = vnode.dom; }}
                            onchange={(e) => {
                                if (e.target.files[0]) this.uploadFile(e.target.files[0]);
                                e.target.value = '';
                            }}
                        />,
                        <div id="chat-limiter"></div>,
                    ]
                )}
            </div>
        );
    }

    updateLimit() {
        const limiter = this.element.querySelector('#chat-limiter');
        if (!limiter) return;

        let charsTyped = this.messageCharLimit - (this.state.input.messageLength || 0);
        limiter.innerText = charsTyped + '/' + this.messageCharLimit;
        limiter.className = charsTyped < 100 ? 'reaching-limit' : '';
    }

    saveDraft(text = this.state.input.content()) {
        this.state.input.lastDraft != text &&
            throttle(300, () => {
                this.state.setChatStorageValue('draft', text);
            })();
        this.state.input.lastDraft = text;
    }

    inputSaveDraft(e) {
        if (e) e.redraw = false;

        let input = e.target;
        this.saveDraft(input.value.trim());
    }

    onInputFocus() {
        if (app.screen() === 'phone') {
            this.expanded = true;
            m.redraw();
        }
    }

    onInputBlur() {
        // Delay to allow click on send/upload buttons to register
        setTimeout(() => {
            if (this.expanded && document.activeElement?.id !== 'chat-input') {
                this.collapseInput();
            }
        }, 200);
    }

    collapseInput() {
        this.expanded = false;
        m.redraw();
    }

    resizeInput() {
        let input = this.state.getChatInput();
        const maxRows = app.screen() === 'phone' ? (this.expanded ? 8 : 2) : 5;

        input.rows = 1;
        this.state.input.rows = Math.min(input.scrollHeight / input.lineHeight, maxRows);
        input.rows = this.state.input.rows;
    }

    uploadFile(file) {
        if (this.uploading) return;
        this.uploading = true;
        m.redraw();

        const body = new FormData();
        body.append('files[]', file);

        app.request({
            method: 'POST',
            url: app.forum.attribute('apiUrl') + '/fof/upload',
            serialize: raw => raw,
            body,
        }).then(response => {
            this.uploading = false;
            const url = response.data?.[0]?.attributes?.url
                || response.data?.attributes?.url;
            if (url) {
                const input = this.state.getChatInput();
                const current = input.value;
                const insertText = current.length ? '\n' + url : url;
                input.value = current + insertText;
                this.state.input.content(input.value);
                this.inputProcess({ target: input, type: 'input' });
                input.focus();
            }
            m.redraw();
        }).catch(() => {
            this.uploading = false;
            app.alerts.show({ type: 'error' }, 'Upload failed');
            m.redraw();
        });
    }

    loadEmojiOneArea() {
        if (window.jQuery && window.jQuery.fn.emojioneArea) return Promise.resolve();
        return new Promise((resolve) => {
            const css = document.createElement('link');
            css.rel = 'stylesheet';
            css.href = 'https://cdn.jsdelivr.net/npm/emojionearea@3.4.2/dist/emojionearea.min.css';
            document.head.appendChild(css);

            // Load jquery-textcomplete first (emojionearea dependency) - must use v1.3.4 UMD build
            const tcScript = document.createElement('script');
            tcScript.src = 'https://cdn.jsdelivr.net/npm/jquery-textcomplete@1.3.4/dist/jquery.textcomplete.min.js';
            tcScript.onload = () => {
                const script = document.createElement('script');
                script.src = 'https://cdn.jsdelivr.net/npm/emojionearea@3.4.2/dist/emojionearea.min.js';
                script.onload = resolve;
                document.body.appendChild(script);
            };
            document.body.appendChild(tcScript);
        });
    }

    toggleEmojiPicker() {
        if (this.emojiAreaLoading) return;

        if (this.emojiArea && this.emojiArea.isReady) {
            if (this.emojiPickerVisible) {
                this.emojiArea.hidePicker();
                this.emojiPickerVisible = false;
            } else {
                this.emojiArea.showPicker();
                this.emojiPickerVisible = true;
            }
            return;
        }

        if (this.emojiArea) return; // still initializing

        this.emojiAreaLoading = true;
        m.redraw();

        this.loadEmojiOneArea().then(() => {
            const container = document.createElement('div');
            container.className = 'ChatInput-emojiContainer';
            this.element.appendChild(container);

            const holder = $('<div />').emojioneArea({
                standalone: true,
                sprite: false,
                hidePickerOnBlur: false,
                container: $(container),
                events: {
                    emojibtn_click: () => {
                        const emoji = this.emojiArea.getText();
                        const input = this.state.getChatInput();
                        const pos = input.selectionStart;
                        const before = input.value.substring(0, pos);
                        const after = input.value.substring(pos);
                        input.value = before + emoji + after;
                        this.state.input.content(input.value);
                        input.selectionStart = input.selectionEnd = pos + emoji.length;
                        this.inputProcess({ target: input, type: 'input' });
                        input.focus();
                    },
                    ready: () => {
                        this.emojiArea.showPicker();
                        this.emojiPickerVisible = true;
                        this.emojiAreaLoading = false;
                        m.redraw();
                    },
                },
            });

            this.emojiArea = holder.data('emojioneArea');

            document.addEventListener('click', this._emojiClickOutside = (e) => {
                const target = $(e.target);
                if (this.emojiPickerVisible &&
                    !target.closest('.emojionearea').length &&
                    !target.closest('.icon.emoji').length) {
                    this.emojiArea.hidePicker();
                    this.emojiPickerVisible = false;
                }
            });
        });
    }

    inputProcess(e) {
        if (e) e.redraw = false;

        // Detect pasted images
        if (e.type === 'paste' && e.clipboardData && e.clipboardData.files.length) {
            const file = e.clipboardData.files[0];
            if (file.type.startsWith('image/')) {
                e.preventDefault();
                this.uploadFile(file);
                return;
            }
        }

        let input = e.target;
        this.state.input.content(input.value);
        let inputValue = input.value.trim();
        this.state.input.messageLength = inputValue.length;
        this.updateLimit();

        this.resizeInput();

        if (this.state.input.messageLength) {
            if (!this.state.input.writingPreview && !this.state.messageEditing) this.inputPreviewStart(inputValue);
        } else {
            if (this.state.input.writingPreview && !inputValue.length) this.inputPreviewEnd();
        }

        if (this.state.messageEditing) this.state.messageEditing.content = inputValue;
        else if (this.state.input.writingPreview) this.state.input.previewModel.content = inputValue;

        this.detectMention(input);

        if (this.attrs.oninput) this.attrs.oninput(e);
    }

    detectMention(input) {
        const cursor = input.selectionStart;
        const text = input.value.substring(0, cursor);

        // Detect game mention with jeu:
        const gameMatch = text.match(/jeu:\s?([\p{L}\p{N}\p{M}\s'_-]*)$/iu);
        if (gameMatch && (gameMatch.index === 0 || /\s/.test(text[gameMatch.index - 1]))) {
            const query = gameMatch[1].trim();
            this.mentionStart = gameMatch.index;
            this.mentionCursorEnd = cursor;
            this.mentionType = 'game';
            this.mentionIndex = 0;

            if (query.length >= 2) {
                clearTimeout(this.gameSearchTimeout);
                this.gameSearchTimeout = setTimeout(() => {
                    app.request({
                        method: 'GET',
                        url: app.forum.attribute('apiUrl') + '/game-mentions',
                        params: { search: query },
                    }).then(response => {
                        this.mentionSuggestions = (response.data || []).map(g => ({
                            type: 'game', id: g.id, name: g.name, slug: g.slug,
                            description: g.description, image_url: g.image_url,
                        }));
                        if (!this.mentionSuggestions.length) this.mentionSuggestions = null;
                        m.redraw();
                    }).catch(() => {
                        this.mentionSuggestions = null;
                        m.redraw();
                    });
                }, 250);
            } else {
                this.mentionSuggestions = null;
            }
            m.redraw();
            return;
        }

        // Detect user mention with @
        const match = text.match(/@(\w*)$/);
        if (match && (match.index === 0 || /\s/.test(text[match.index - 1]))) {
            const query = match[1].toLowerCase();
            this.mentionStart = match.index;
            this.mentionType = 'user';
            this.mentionSuggestions = this.filterUsers(query);
            this.mentionIndex = 0;
        } else {
            this.mentionSuggestions = null;
            this.mentionType = null;
        }
        m.redraw();
    }

    filterUsers(query) {
        const users = this.model.users();
        if (!users) return null;

        const filtered = users.filter(u => {
            if (!u || u === app.session.user) return false;
            if (!query) return true;
            return u.username().toLowerCase().startsWith(query)
                || u.displayName().toLowerCase().startsWith(query);
        }).slice(0, 5);

        return filtered.length ? filtered : null;
    }

    inputKeyDown(e) {
        if (!this.mentionSuggestions || !this.mentionSuggestions.length) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            this.mentionIndex = (this.mentionIndex + 1) % this.mentionSuggestions.length;
            m.redraw();
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            this.mentionIndex = (this.mentionIndex - 1 + this.mentionSuggestions.length) % this.mentionSuggestions.length;
            m.redraw();
        } else if (e.key === 'Enter' || e.key === 'Tab') {
            e.preventDefault();
            e.stopPropagation();
            this.selectMention(this.mentionSuggestions[this.mentionIndex]);
        } else if (e.key === 'Escape') {
            e.preventDefault();
            this.mentionSuggestions = null;
            m.redraw();
        }
    }

    selectMention(item) {
        const input = this.state.getChatInput();
        const isGame = item.type === 'game';
        const before = input.value.substring(0, this.mentionStart);
        const after = input.value.substring(isGame ? this.mentionCursorEnd : input.selectionStart);
        let mention;

        if (isGame) {
            mention = 'jeu:\u201C' + item.name + '\u201D#' + item.id + ' ';
        } else {
            mention = '@' + item.username() + ' ';
        }

        input.value = before + mention + after;
        this.state.input.content(input.value);
        input.selectionStart = input.selectionEnd = this.mentionStart + mention.length;
        this.mentionSuggestions = null;
        this.mentionType = null;
        input.focus();
        this.inputProcess({ target: input, type: 'input' });
        m.redraw();
    }

    inputPressEnter(e) {
        e.redraw = false;
        if (e.keyCode == 13 && !e.shiftKey) {
            if (this.mentionSuggestions && this.mentionSuggestions.length) {
                return false;
            }
            this.state.messageSend();
            return false;
        }
        return true;
    }

    inputPressButton() {
        this.state.messageSend();
    }

    inputPreviewStart(content) {
        if (!this.state.input.writingPreview) {
            this.state.input.writingPreview = true;

            this.state.input.previewModel = app.store.createRecord('chatmessages');
            this.state.input.previewModel.pushData({
                id: 0,
                attributes: { message: ' ', created_at: 0 },
                relationships: { user: app.session.user, chat: this.model },
            });
            Object.assign(this.state.input.previewModel, { isEditing: true, isNeedToFlash: true, content });
        } else this.state.input.previewModel.isNeedToFlash = true;

        m.redraw();
    }

    inputPreviewEnd() {
        this.state.input.writingPreview = false;

        m.redraw();
    }
}
